// src/components/parent-dashboard/components/PeerAssessment.js
import React from "react";

/**
 * Displays the peer assessment (read-only)
 * Shows how another student assessed the child's work
 */
const PeerAssessment = ({peerAssessment}) => {
    if (!peerAssessment) {
        return (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h2 className="text-lg leading-6 font-medium text-gray-900">Peer Assessment</h2>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        Another student's review of your child's work
                    </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <div className="text-center py-6">
                        <div className="h-10 w-10 text-gray-400 mx-auto mb-3">👥</div>
                        <p className="text-sm text-gray-500">
                            No peer assessment available yet
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Ensure friend_needed is an array to prevent undefined.includes errors
    const friendNeededResources = peerAssessment.friend_needed || [];

    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 bg-gray-50">
                <h2 className="text-lg leading-6 font-medium text-gray-900">Peer Assessment</h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Another student's review of your child's work
                </p>
            </div>
            <div className="border-t border-gray-200">
                <div className="flex flex-col sm:flex-row">
                    {/* Left sidebar */}
                    <div className="bg-blue-700 text-white p-4 flex-shrink-0 sm:w-40 flex items-center justify-center">
                        <h3 className="text-xl font-bold text-center">Peer Assessment</h3>
                    </div>

                    {/* Main content in horizontal layout */}
                    <div className="flex-grow flex flex-col sm:flex-row">
                        {/* "My friend liked doing this work" section */}
                        <div className="sm:flex-1 bg-green-500">
                            <div className="text-white p-3 text-center font-medium">
                                My friend liked doing this work.
                            </div>
                            <div className="flex justify-around p-4">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${peerAssessment.friend_liked === true ? 'bg-white' : 'bg-green-400'}`}>
                                        😊
                                    </div>
                                    <span className="mt-2 text-white text-sm">yes</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${peerAssessment.friend_liked === false ? 'bg-white' : 'bg-green-400'}`}>
                                        😔
                                    </div>
                                    <span className="mt-2 text-white text-sm">no</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${peerAssessment.friend_liked === null ? 'bg-white border-2 border-white' : 'bg-green-400'}`}>
                                        🤔
                                    </div>
                                    <span className="mt-2 text-white text-sm text-center">do not<br/>know</span>
                                </div>
                            </div>
                        </div>

                        {/* "My friend found this work easy" section */}
                        <div className="sm:flex-1 bg-pink-500">
                            <div className="text-white p-3 text-center font-medium">
                                My friend found this work easy.
                            </div>
                            <div className="flex justify-around p-4">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${peerAssessment.friend_easy === true ? 'bg-white' : 'bg-pink-400'}`}>
                                        😊
                                    </div>
                                    <span className="mt-2 text-white text-sm">yes</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${peerAssessment.friend_easy === false ? 'bg-white' : 'bg-pink-400'}`}>
                                        😔
                                    </div>
                                    <span className="mt-2 text-white text-sm">no</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${peerAssessment.friend_easy === null ? 'bg-white border-2 border-white' : 'bg-pink-400'}`}>
                                        🤔
                                    </div>
                                    <span className="mt-2 text-white text-sm text-center">do not<br/>know</span>
                                </div>
                            </div>
                        </div>

                        {/* "To do this work, my friend needed..." section */}
                        <div className="sm:flex-1 bg-blue-500">
                            <div className="text-white p-3 text-center font-medium">
                                To do this work, my friend needed...
                            </div>
                            <div className="p-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${friendNeededResources.includes("classmate") ? 'bg-white' : 'bg-blue-400'}`}>
                                            👥
                                        </div>
                                        <span className="mt-2 text-white text-sm text-center">Classmate</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${friendNeededResources.includes("teacher") ? 'bg-white' : 'bg-blue-400'}`}>
                                            👨‍🏫
                                        </div>
                                        <span className="mt-2 text-white text-sm text-center">Teacher</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${friendNeededResources.includes("books") ? 'bg-white' : 'bg-blue-400'}`}>
                                            📚
                                        </div>
                                        <span className="mt-2 text-white text-sm text-center">Books</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${friendNeededResources.includes("computer") ? 'bg-white' : 'bg-blue-400'}`}>
                                            💻
                                        </div>
                                        <span className="mt-2 text-white text-sm text-center">Computer</span>
                                    </div>

                                    <div className="flex flex-col items-center col-span-2 mx-auto">
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${friendNeededResources.includes("none") ? 'bg-white' : 'bg-blue-400'}`}>
                                            ⛔
                                        </div>
                                        <span className="mt-2 text-white text-sm text-center">None</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PeerAssessment;